import { syncBankAccountsAndImport } from "@/apis/custom/customRoutes";
import { ErrorMessage } from "@/components/ui/error";
import { useGetAllBankAccounts } from "@/queries/bank-accounts/get-all";
import { useUpdateBankAccount } from "@/queries/bank-accounts/update";
import { useDeleteBankConnection } from "@/queries/bank-connections/delete";
import { useGetAllBankConnections } from "@/queries/bank-connections/get-all";
import { useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BankComponent } from "./bank";

export const BankConnections = () => {
  const {
    bankConnections,
    loading: bankConnectionsLoading,
    error: bankConnectionError,
    refetch: refetchBankConnections,
  } = useGetAllBankConnections();
  const {
    bankAccounts,
    loading: bankAccountsLoading,
    error: bankAccountsError,
    refetch: refetchBankAccounts,
  } = useGetAllBankAccounts();

  const { deleteBankConnection } = useDeleteBankConnection();
  const [updateDefaultAccount] = useUpdateBankAccount();

  const { t } = useTranslation();

  const handleRefetch = () => {
    refetchBankConnections();
    refetchBankAccounts();
  };

  usePollConnectionsWithNoAccounts();
  useFeedbackForReauth();

  const error = bankConnectionError || bankAccountsError;
  const loading = bankConnectionsLoading || bankAccountsLoading;

  if (error) {
    return (
      <div className="flex-1 flex justify-center items-center h-full">
        <ErrorMessage retry={handleRefetch} />
      </div>
    );
  }

  const handleDeleteBankConnection = (bankConnectionId: string) => {
    deleteBankConnection(bankConnectionId);
  };

  const handleUpdateDefaultAccount = (
    accountId: string,
    isCurrentlyDefault: boolean,
  ) => {
    // If clicking on already default account, uncheck it. Otherwise, set it as default
    updateDefaultAccount(
      {
        bankAccountId: accountId,
        defaultAccount: !isCurrentlyDefault,
      },
      () => {
        // Refetch to ensure all bank connections reflect the change
        handleRefetch();
      },
    );
  };

  const handleSetJournalAccount = async (journalAccountId: string) => {
    await handleRefetch();
  };

  return (
    <BankComponent
      bankConnections={bankConnections}
      bankAccounts={bankAccounts}
      loading={loading}
      onDeleteBankConnection={handleDeleteBankConnection}
      onUpdateDefaultAccount={handleUpdateDefaultAccount}
      onSetJournalAccount={handleSetJournalAccount}
    />
  );

  function usePollConnectionsWithNoAccounts() {
    // A connection without accounts is not valid, so we need to poll for updates
    useEffect(() => {
      const hasConnectionWithNoAccounts = bankConnections?.some(
        (connection) => !connection.accounts?.length,
      );

      let pollingInterval: NodeJS.Timeout | null = null;

      if (hasConnectionWithNoAccounts) {
        pollingInterval = setInterval(() => {
          handleRefetch();
        }, 5000);
      }

      return () => {
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      };
    }, [bankConnections, handleRefetch]);
  }

  function useFeedbackForReauth() {
    const search = useSearch({ strict: false });
    const reauthSuccess = (search as { reauthSuccess?: string }).reauthSuccess;
    const reauthError = (search as { reauthError?: string }).reauthError;
    const reauthAbort = (search as { reauthAbort?: string }).reauthAbort;

    useEffect(() => {
      const handleReauthFeedback = async () => {
        if (reauthSuccess === "true") {
          toast.info(t("bank_connections.toasts.sync_in_progress"));
          await syncBankAccountsAndImport();
          await handleRefetch();
          toast.success(t("bank_connections.toasts.sync_success"));
        }

        if (reauthError === "true") {
          toast.error(t("bank_connections.toasts.reauth_error"));
        }

        if (reauthAbort === "true") {
          toast.info(t("bank_connections.toasts.connection_aborted"));
        }
      };

      handleReauthFeedback();
    }, [reauthSuccess, reauthError, reauthAbort]);
  }
};
